import {
  coreActions,
  ShowAuthAlertBannerStatus,
  ShowCookieAlertBannerStatus,
} from '@core/actions/core.actions';
import { stageActions, stageStudyActions } from '@core/actions/stage.actions';
import { authApiActions } from '@core/auth/auth.actions';
import { createReducer, on } from '@ngrx/store';
import { projectDetailActions } from '@core/actions/project-detail.actions';

export enum ResetPasswordStatus {
  idle,
  sending,
  sent,
  failed,
}

export interface ApplicationUIState {
  updatePending: boolean;
  // Counts include queued stage/settings and question writes, including their live retries.
  stageAdminWrites?: Record<string, number>;
  showDebuggers: boolean;
  diagnosticLogging: boolean;
  showChangeDetection: boolean;
  resetPasswordEmailStatus: ResetPasswordStatus;
  authAlertBannerStatus: ShowAuthAlertBannerStatus;
  cookieAlertBannerStatus: ShowCookieAlertBannerStatus;
  minUIVersion: string | null;
}

const initialApplicationUIState: ApplicationUIState = {
  updatePending: false,
  showDebuggers: false,
  diagnosticLogging: false,
  showChangeDetection: false,
  resetPasswordEmailStatus: ResetPasswordStatus.idle,
  authAlertBannerStatus: ShowAuthAlertBannerStatus.false,
  cookieAlertBannerStatus: ShowCookieAlertBannerStatus.false,
  minUIVersion: null,
};

export const applicationUiReducer = createReducer(
  initialApplicationUIState,
  on(
    projectDetailActions.updateStage,
    projectDetailActions.updateStageQuestions,
    (state, { projectId }): ApplicationUIState => ({
      ...state,
      stageAdminWrites: {
        ...state.stageAdminWrites,
        [projectId]: (state.stageAdminWrites?.[projectId] ?? 0) + 1,
      },
    })
  ),
  on(
    projectDetailActions.stageAdminWriteCompleted,
    (state, { projectId }): ApplicationUIState => ({
      ...state,
      stageAdminWrites: {
        ...state.stageAdminWrites,
        [projectId]: Math.max(
          0,
          (state.stageAdminWrites?.[projectId] ?? 0) - 1
        ),
      },
    })
  ),
  on(
    coreActions.setChangeDetectionVisualisation,
    (state, props): ApplicationUIState => ({
      ...state,
      showChangeDetection: props.visualiseChangeDetection,
    })
  ),
  on(
    coreActions.setLogging,
    (state, props): ApplicationUIState => ({
      ...state,
      diagnosticLogging: props.showLogging,
    })
  ),
  on(
    coreActions.setDebuggers,
    (state, props): ApplicationUIState => ({
      ...state,
      showDebuggers: props.showDebuggers,
    })
  ),
  on(
    coreActions.setAuthAlertDisplayStatus,
    (state, props): ApplicationUIState => ({
      ...state,
      authAlertBannerStatus: props.status,
    })
  ),
  on(
    coreActions.coreInit,
    (state, props): ApplicationUIState => ({
      ...state,
      cookieAlertBannerStatus: props.cookieAlertBannerStatus,
      authAlertBannerStatus: props.authAlertStatus,
    })
  ),
  on(
    coreActions.setCookieAlertDisplayStatus,
    (state, props): ApplicationUIState => ({
      ...state,
      cookieAlertBannerStatus: props.status,
    })
  ),
  on(
    projectDetailActions.updateAgreementMode,
    projectDetailActions.deleteProject,
    projectDetailActions.addStage,
    projectDetailActions.removeSearch,
    projectDetailActions.deleteSearchImportJob,
    projectDetailActions.loadProjectListings,
    projectDetailActions.updateStage,
    projectDetailActions.updateStageQuestions,
    projectDetailActions.update,
    projectDetailActions.updateProjectMembershipGroups,
    stageActions.loadStageStudy,
    stageActions.getNextStudyForStage,
    stageActions.submitScreeningAndNextStudy,
    (state): ApplicationUIState => ({
      ...state,
      ...{
        updatePending: true,
      },
    })
  ),
  on(stageActions.deleteSession, stageActions.saveSession, (state, props) =>
    props.correlationId !== undefined
      ? state
      : {
          ...state,
          updatePending: true,
        }
  ),
  on(
    projectDetailActions.agreementModeUpdated,
    projectDetailActions.projectDeleted,
    projectDetailActions.projectDeletionFailed,
    projectDetailActions.stageAdded,
    projectDetailActions.searchRemoved,
    projectDetailActions.searchRemovalFailed,
    projectDetailActions.searchDeletionRequested,
    projectDetailActions.searchImportJobDeleted,
    projectDetailActions.searchImportJobDeletionFailed,
    projectDetailActions.registered,
    projectDetailActions.projectListingsLoaded,
    projectDetailActions.stageUpdated,
    projectDetailActions.stageUpdateFailed,
    projectDetailActions.stageQuestionsUpdated,
    projectDetailActions.projectUpdated,
    projectDetailActions.projectListingUpdated,
    projectDetailActions.projectMembershipGroupsUpdated,
    stageActions.incompleteSessionsUpdated,
    stageActions.receivedFullStats,
    stageStudyActions.receivedStageStudy,
    projectDetailActions.screeningSummaryReceived,
    stageStudyActions.noNewStudiesForStage,
    (state): ApplicationUIState => ({
      ...state,
      updatePending: false,
    })
  ),
  on(stageActions.sessionSaved, (state, props) =>
    props.correlationId !== undefined
      ? state
      : {
          ...state,
          updatePending: false,
        }
  ),
  on(
    authApiActions.sendPasswordResetEmail,
    (state): ApplicationUIState => ({
      ...state,
      resetPasswordEmailStatus: ResetPasswordStatus.sending,
    })
  ),
  on(
    authApiActions.passwordEmailResetFailed,
    (state): ApplicationUIState => ({
      ...state,
      resetPasswordEmailStatus: ResetPasswordStatus.failed,
    })
  ),
  on(
    authApiActions.passwordEmailResetSent,
    (state): ApplicationUIState => ({
      ...state,
      resetPasswordEmailStatus: ResetPasswordStatus.sent,
    })
  )
);
